import type { PlayerSeason, PositionTag } from "../data/players";
import type { LineupSlot } from "./types";

const maxEffectivePositionTags = 3;

export function isSlotCompatible(player: PlayerSeason, slot: LineupSlot) {
  if (!player.roles.includes(slot.role)) {
    return false;
  }

  const positionTags = getEffectivePositionTags(player);

  return slot.positionTags.some((tag) => positionTags.includes(tag));
}

export function slotFitScore(player: PlayerSeason, slot: LineupSlot) {
  if (!player.roles.includes(slot.role)) {
    return -100;
  }

  const positionTags = getEffectivePositionTags(player);

  return slot.positionTags.reduce(
    (score, tag, index) => score + (positionTags.includes(tag) ? 10 - index : 0),
    0,
  );
}

export function sortSlotsByFit(player: PlayerSeason, slots: LineupSlot[]) {
  return [...slots].sort(
    (left, right) =>
      slotFitScore(player, right) - slotFitScore(player, left) ||
      left.id.localeCompare(right.id),
  );
}

export function getEffectivePositionTags(player: PlayerSeason): PositionTag[] {
  if (player.positionTags && player.positionTags.length > 0) {
    return uniqueTags(player.positionTags).slice(0, maxEffectivePositionTags);
  }

  return inferPositionTags(player).slice(0, maxEffectivePositionTags);
}

function inferPositionTags(player: PlayerSeason): PositionTag[] {
  const text = normalizePositionText(`${player.name} ${player.tag} ${player.note}`);
  const roles = new Set(player.roles);

  if (roles.has("POR")) {
    return ["portiere"];
  }

  if (roles.has("DIF") && roles.has("CEN")) {
    if (hasWideClue(text)) {
      return ["terzino", "quinto", "esterno"];
    }

    if (hasRegistaClue(text)) {
      return ["regista", "mediano", "centrale"];
    }

    if (hasDefensiveMidClue(text)) {
      return ["mediano", "centrale", "braccetto"];
    }

    return ["centrale", "braccetto", "mediano"];
  }

  if (roles.has("CEN") && roles.has("ATT")) {
    if (hasWideClue(text)) {
      return ["ala", "esterno", "seconda-punta"];
    }

    if (hasRegistaClue(text) && !hasSecondStrikerClue(text)) {
      return ["regista", "mezzala", "trequartista"];
    }

    if (hasBoxToBoxClue(text)) {
      return ["mezzala", "trequartista", "seconda-punta"];
    }

    return ["trequartista", "seconda-punta"];
  }

  if (roles.has("DIF")) {
    if (hasWideClue(text)) {
      return ["terzino", "quinto"];
    }

    return ["centrale", "braccetto"];
  }

  if (roles.has("CEN")) {
    if (hasWideClue(text)) {
      return ["esterno", "quinto"];
    }

    if (hasRegistaClue(text)) {
      return ["regista", "mediano"];
    }

    if (hasAttackingMidClue(text)) {
      return ["trequartista", "mezzala"];
    }

    return ["mezzala", "mediano"];
  }

  if (roles.has("ATT")) {
    if (hasWideClue(text)) {
      return ["ala", "seconda-punta"];
    }

    if (hasAttackingMidClue(text)) {
      return ["seconda-punta", "trequartista"];
    }

    return ["centravanti", "seconda-punta"];
  }

  return [];
}

function normalizePositionText(value: string) {
  return value
    .normalize("NFD")
    .replace(/[\u0300-\u036f]/g, "")
    .toLowerCase();
}

function hasWideClue(text: string) {
  return /\b(ala|ali|ampiezza|cross|corsia|esterno|fascia|laterale|quinto|sinistra|sinistro|destra|destro|spinta|strappi|terzin[oi]?|wide)\b/.test(text);
}

function hasRegistaClue(text: string) {
  return /\b(cervello|geometr|lanci|maestro|pirlo|regia|regista|ritmo)\b/.test(text);
}

function hasDefensiveMidClue(text: string) {
  return /\b(cerniera|copertura|equilibr|mediana|mediano|operaio|schermo)\b/.test(text);
}

function hasAttackingMidClue(text: string) {
  return /\b(dieci|fantasia|rifinit|trequart|joja|joya|codino|maestro)\b/.test(text);
}

function hasSecondStrikerClue(text: string) {
  return /\b(seconda punta|mezzapunta|punta raccordo|tra le linee|dietro)\b/.test(text);
}

function hasBoxToBoxClue(text: string) {
  return /\b(inseriment|mezzala|polmone|raccordo)\b/.test(text);
}

function uniqueTags(tags: PositionTag[]) {
  return [...new Set(tags)];
}
